/**
 * Shopify CDN image helpers.
 *
 * Shopify's CDN supports on-the-fly image transformations via query
 * parameters. We use these to serve appropriately sized images instead
 * of always shipping the full 1200×1200 source. This dramatically
 * reduces bytes transferred without changing the visual output.
 *
 * Reference: https://shopify.dev/docs/api/liquid/filters/image_url
 */

const SHOPIFY_CDN = "cdn.shopify.com";

const isShopifyCdn = (url: string): boolean => {
  try {
    const u = new URL(url);
    return u.hostname.endsWith(SHOPIFY_CDN);
  } catch {
    return false;
  }
};

/**
 * Build a Shopify CDN URL with a `width=` (and optional `height=`) query
 * parameter so the CDN returns a resized, optimized version of the asset.
 *
 * Falls back to the original URL for non-Shopify hosts.
 */
export function shopifyImageUrl(
  url: string | null | undefined,
  options: { width?: number; height?: number; crop?: "center" | "top" | "bottom" | "left" | "right" } = {}
): string {
  if (!url) return "";
  if (!isShopifyCdn(url)) return url;

  try {
    const u = new URL(url);
    if (options.width) u.searchParams.set("width", String(Math.round(options.width)));
    if (options.height) u.searchParams.set("height", String(Math.round(options.height)));
    if (options.crop) u.searchParams.set("crop", options.crop);
    return u.toString();
  } catch {
    return url;
  }
}

/**
 * Build a `srcset` string for a Shopify image at multiple widths,
 * suitable for use with the `sizes` attribute.
 */
export function shopifyImageSrcSet(
  url: string | null | undefined,
  widths: number[] = [320, 480, 640, 800, 1024, 1280]
): string {
  if (!url) return "";
  if (!isShopifyCdn(url)) return "";
  return widths
    .map((w) => `${shopifyImageUrl(url, { width: w })} ${w}w`)
    .join(", ");
}
